import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DomainStatus } from "../types";

interface DomainStatusBadgeProps {
  status: DomainStatus;
  createdAt: string;
}

const REMOVAL_GRACE_PERIOD_MS = 24 * 60 * 60 * 1000;

// Statuses that mean the domain hasn't finished verifying yet, so it's still
// on the 24h auto-removal clock.
const UNVERIFIED_STATUSES = new Set<DomainStatus>([
  DomainStatus.PENDING,
  DomainStatus.VERIFICATION_REQUIRED,
  DomainStatus.VERIFYING,
  DomainStatus.PROVISIONING,
  DomainStatus.FAILED,
]);

const formatRemainingTime = (msRemaining: number) => {
  const totalMinutes = Math.max(0, Math.round(msRemaining / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const STATUS_CLASSES: Record<DomainStatus, string> = {
  [DomainStatus.PENDING]: "bg-secondary-subtle text-secondary-emphasis",
  [DomainStatus.VERIFICATION_REQUIRED]: "bg-secondary-subtle text-secondary-emphasis",
  [DomainStatus.VERIFYING]: "bg-primary-subtle text-primary-emphasis",
  [DomainStatus.VERIFIED]: "bg-success-subtle text-success-emphasis",
  [DomainStatus.PROVISIONING]: "bg-primary-subtle text-primary-emphasis",
  [DomainStatus.ACTIVE]: "bg-success-subtle text-success-emphasis",
  [DomainStatus.FAILED]: "bg-danger-subtle text-danger-emphasis",
  [DomainStatus.REMOVING]: "bg-secondary-subtle text-secondary-emphasis",
  [DomainStatus.REMOVED]: "bg-secondary-subtle text-secondary-emphasis",
};

const STATUS_LABEL_KEYS: Record<DomainStatus, string> = {
  [DomainStatus.PENDING]: "customDomains.statusPending",
  [DomainStatus.VERIFICATION_REQUIRED]: "customDomains.statusPendingVerification",
  [DomainStatus.VERIFYING]: "customDomains.statusVerifying",
  [DomainStatus.VERIFIED]: "customDomains.statusVerified",
  [DomainStatus.PROVISIONING]: "customDomains.statusProvisioning",
  [DomainStatus.ACTIVE]: "customDomains.statusActive",
  [DomainStatus.FAILED]: "customDomains.statusFailed",
  [DomainStatus.REMOVING]: "customDomains.statusRemoving",
  [DomainStatus.REMOVED]: "customDomains.statusRemoved",
};

const DomainStatusBadge: React.FC<DomainStatusBadgeProps> = ({ status, createdAt }) => {
  const { t } = useTranslation();

  const tooltipText = (() => {
    if (!UNVERIFIED_STATUSES.has(status)) {
      return null;
    }

    const msRemaining = new Date(createdAt).getTime() + REMOVAL_GRACE_PERIOD_MS - Date.now();

    if (msRemaining <= 0) {
      return t("customDomains.removalImminent");
    }

    return t("customDomains.removalTimeRemaining", {
      time: formatRemainingTime(msRemaining),
    });
  })();

  const badge = (
    <span className={`badge rounded-pill fw-normal px-2 py-1 ${STATUS_CLASSES[status]}`}>
      <span
        className="d-inline-block rounded-circle me-1"
        style={{ width: 6, height: 6, backgroundColor: "currentColor" }}
      />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );

  if (!tooltipText) {
    return <div>{badge}</div>;
  }

  return (
    <div>
      <OverlayTrigger placement="top" overlay={<Tooltip>{tooltipText}</Tooltip>}>
        <span>{badge}</span>
      </OverlayTrigger>
    </div>
  );
};

export default DomainStatusBadge;
